import { AnimatePresence, motion } from 'framer-motion';
import { Briefcase, Clock, Download, GraduationCap, Lock, MapPin } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

// ─── Brochure Download Modal ──────────────────────────────────────────────────
const BROCHURE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycby50D6mWGNToQR2AOOqWM-hV568hmTZV9TKJukPBzLt3SUZJ_nNBte2chAMCe2OVVmS/exec';

interface BrochureModalProps {
  course: { title: string; file: string } | null;
  onClose: () => void;
}

const BrochureModal = ({ course, onClose }: BrochureModalProps) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', qualification: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  if (!course) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) { setError('Please fill all required fields.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const payload = new FormData();
      payload.append('name', form.name);
      payload.append('email', form.email);
      payload.append('phone', form.phone);
      payload.append('qualification', form.qualification);
      payload.append('courseName', course.title);
      payload.append('timestamp', new Date().toISOString());
      payload.append('source', 'Brochure Download - Courses Page');
      await fetch(BROCHURE_SCRIPT_URL, { method: 'POST', body: payload, mode: 'no-cors' });
    } catch { /* allow download regardless */ }

    setSubmitted(true);
    setSubmitting(false);
    const link = document.createElement('a');
    link.href = course.file;
    link.download = `${course.title} Brochure.pdf`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={(e) => { if (e.target === e.currentTarget) { onClose(); } }}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(13,11,20,0.75)',
          backdropFilter: 'blur(10px)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9 }}
          style={{
            background: 'white', borderRadius: '24px', padding: '40px',
            width: '100%', maxWidth: '480px', position: 'relative',
            boxShadow: '0 30px 80px rgba(0,0,0,0.3)',
          }}
        >
          <button onClick={onClose} style={{
            position: 'absolute', top: '16px', right: '16px',
            width: '32px', height: '32px', borderRadius: '50%',
            background: '#f3f4f6', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>

          {!submitted ? (
            <>
              <div style={{ marginBottom: '28px' }}>
                <div style={{ display: 'inline-flex', padding: '12px', borderRadius: '14px', background: '#f0edff', color: '#2b2240', marginBottom: '12px' }}><Download size={28} /></div>
                <h3 style={{ fontSize: '1.4rem', fontWeight: '800', color: '#2b2240', marginBottom: '6px' }}>Download Brochure</h3>
                <p style={{ color: '#6b7280', fontSize: '0.875rem', lineHeight: 1.5 }}>
                  Get the full <strong>{course.title}</strong> brochure — curriculum, fees, placement process & more.
                </p>
              </div>

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {[
                  { key: 'name', label: 'Full Name *', type: 'text', placeholder: 'Your full name' },
                  { key: 'email', label: 'Email Address *', type: 'email', placeholder: 'your@email.com' },
                  { key: 'phone', label: 'Phone Number *', type: 'tel', placeholder: '+91 XXXXX XXXXX' },
                  { key: 'qualification', label: 'Highest Qualification', type: 'text', placeholder: 'B.Tech / MCA / BCA ...' },
                ].map((f) => (
                  <div key={f.key}>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '600', color: '#374151', marginBottom: '5px' }}>{f.label}</label>
                    <input
                      type={f.type}
                      placeholder={f.placeholder}
                      value={(form as any)[f.key]}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1.5px solid #e5e7eb', fontSize: '0.875rem', outline: 'none', fontFamily: 'Poppins, sans-serif', boxSizing: 'border-box' }}
                      onFocus={(e) => { e.target.style.borderColor = '#2b2240'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; }}
                    />
                  </div>
                ))}
                {error && <p style={{ color: '#ef4444', fontSize: '0.8rem' }}>{error}</p>}
                <button
                  type="submit"
                  disabled={submitting}
                  style={{ padding: '13px', borderRadius: '10px', border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #2b2240, #4e3a69)', color: 'white', fontWeight: '700', fontSize: '0.95rem', fontFamily: 'Poppins, sans-serif', opacity: submitting ? 0.7 : 1 }}
                >
                  {submitting ? 'Processing...' : <><Download size={16} style={{ marginRight: '7px', display: 'inline' }} />Download Brochure Now</>}
                </button>
                <p style={{ textAlign: 'center', fontSize: '0.72rem', color: '#9ca3af', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}><Lock size={11} />Your info is private. No spam, ever.</p>
              </form>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', duration: 0.5 }}>
                <div style={{ display: 'inline-flex', padding: '16px', borderRadius: '50%', background: '#d1fae5', color: '#059669', marginBottom: '16px' }}><Download size={36} /></div>
              </motion.div>
              <h3 style={{ fontSize: '1.4rem', fontWeight: '800', color: '#2b2240', marginBottom: '8px' }}>Download Started!</h3>
              <p style={{ color: '#6b7280', marginBottom: '24px', fontSize: '0.875rem' }}>
                Your <strong>{course.title}</strong> brochure is downloading. Our team will reach out soon!
              </p>
              <button onClick={onClose} style={{ padding: '12px 28px', borderRadius: '10px', border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #2b2240, #4e3a69)', color: 'white', fontWeight: '700', fontFamily: 'Poppins, sans-serif' }}>
                Close
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ─── Courses Page ─────────────────────────────────────────────────────────────
const Courses = () => {
  const [brochureCourse, setBrochureCourse] = useState<{ title: string; file: string } | null>(null);

  const courses = [
    {
      id: 'aiml',
      title: 'GEN AI - AIML',
      duration: '45 Days',
      mode: 'Online & Offline',
      description: 'Go from Python foundations to production AI systems — classical ML, deep learning, NLP, computer vision, generative models and MLOps deployment.',
      domains: ['Python', 'SQL', 'Machine Learning', 'Deep Learning'],
      modules: [
        'Foundations - Python, Math & Data Handling',
        'Classical Machine Learning',
        'Deep Learning Foundations',
        'Training, Experiment Tracking & NLP',
        'Computer Vision & Generative Models',
        'MLOps & Deployment',
        'Capstone & Career Prep',
      ],
      placementRoles: ['Machine Learning Engineer', 'AI Engineer', 'Deep Learning Engineer', 'NLP Engineer', 'Computer Vision Engineer', 'MLOps Engineer', 'Generative AI Engineer', 'Data Scientist (ML Focus)'],
      gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
      color: '#8b5cf6',
      file: '/assets/Brochure/GEN AI - AIML Brochure.pdf',
    },
    {
      id: 'full-stack-analytics',
      title: 'GEN AI - Full Stack Analytics',
      duration: '60 Days',
      mode: 'Online & Offline',
      description: 'Become a job-ready Business Analyst — Excel, SQL, Python, Power BI, Tableau and industry tools like Jira, Trello and Confluence.',
      domains: ['Excel', 'Power BI', 'SQL', 'Python', 'Tableau', 'Jira'],
      modules: [
        'Business Analyst Foundations',
        'Excel & Google Sheets for Analytics',
        'SQL for Business Analysis',
        'Python for Data Analytics',
        'Statistics & Exploratory Data Analysis',
        'Power BI',
        'Tableau',
        'BA Tools & Career Prep',
        'Final Capstone Project',
      ],
      placementRoles: ['Business Analyst', 'Data Analyst', 'Reporting Analyst', 'MIS Executive / MIS Analyst', 'Power BI Developer / Analyst', 'Tableau Developer / Analyst', 'Business Intelligence (BI) Analyst', 'Operations Analyst'],
      gradient: 'linear-gradient(135deg, #14b8a6, #0d9488)',
      color: '#14b8a6',
      file: '/assets/Brochure/GEN AI - Full Stack Analytics  Brochure.pdf',
    },
  ];

  // Same for every course (group sessions). The Rs. 1,000 enrollment fee is
  // the first part of the before-placement half, not an extra charge.
  const feeModes = [
    { mode: 'Online', before: 'Rs. 15,000', after: 'Rs. 15,000', total: 'Rs. 30,000' },
    { mode: 'Offline (in person)', before: 'Rs. 20,000', after: 'Rs. 20,000', total: 'Rs. 40,000' },
  ];

  const commonBenefits = [
    'Free Gen AI Training', 'Free Agent AI (n8n)', '3+ Industry Projects',
    '2+ Agent Projects', 'Personalized Timing',
    'Technical Evaluation', 'Weekly Strategic Review', 'Performance Rewards',
    'Internal Internship', 'Placement till 1st Offer', '50% Pay After Placement',
  ];

  return (
    <div className="min-h-screen font-poppins bg-white" style={{ overflowX: 'hidden' }}>
      <BrochureModal course={brochureCourse} onClose={() => setBrochureCourse(null)} />

      {/* Hero */}
      <section style={{
        background: 'linear-gradient(135deg, #0d0b14 0%, #2b2240 50%, #1a1030 100%)',
        padding: '100px 1.5rem 80px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)', backgroundSize: '60px 60px', pointerEvents: 'none' }} />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          style={{ maxWidth: '700px', margin: '0 auto', position: 'relative', zIndex: 1 }}
        >
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(240,180,41,0.15)', border: '1px solid rgba(240,180,41,0.35)', borderRadius: '50px', padding: '6px 20px', marginBottom: '24px', color: '#F0B429', fontSize: '0.875rem', fontWeight: '600' }}>
            <GraduationCap size={15} /> Industry-Ready Programs
          </div>
          <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3.2rem)', fontWeight: '900', color: 'white', lineHeight: 1.2, marginBottom: '16px' }}>
            2 Gen AI Courses with{' '}
            <span style={{ background: 'linear-gradient(135deg, #F0B429, #FF6535)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              50% Pay After Placement
            </span>
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '1.05rem', lineHeight: 1.7, marginBottom: '32px' }}>
            India's first institute to offer <strong style={{ color: 'rgba(255,255,255,0.9)' }}>50% Pay After Placement</strong>. 60-day intensive programs with AI-powered tools and real projects.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap', fontSize: '0.875rem' }}>
            {['50% Pay After Placement', 'Intensive Program', 'Capstone Project'].map((t) => (
              <span key={t} style={{ padding: '6px 16px', borderRadius: '50px', background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.8)', border: '1px solid rgba(255,255,255,0.15)' }}>{t}</span>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Courses Grid */}
      <section style={{ padding: '60px 1.5rem' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div className="grid-2col" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }}>
            {courses.map((course, index) => (
              <motion.div
                key={course.id}
                id={course.id}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.07 }}
                style={{
                  background: 'white', border: '1.5px solid #f0f0f0', borderRadius: '20px',
                  overflow: 'hidden', boxShadow: '0 2px 16px rgba(43,34,64,0.06)',
                }}
              >
                <div style={{ height: '4px', background: course.gradient }} />
                <div style={{ padding: '28px' }}>
                  <h3 style={{ fontSize: '1.15rem', fontWeight: '800', color: '#1f2937', marginBottom: '14px', lineHeight: 1.3 }}>{course.title}</h3>

                  <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', fontSize: '0.78rem', color: '#6b7280', alignItems: 'center' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Clock size={12} />{course.duration}</span>
                    <span>|</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><MapPin size={12} />{course.mode}</span>
                  </div>

                  <p style={{ color: '#4b5563', fontSize: '0.875rem', lineHeight: 1.65, marginBottom: '16px' }}>{course.description}</p>

                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#2b2240', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Domains Covered</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {course.domains.map((d) => (
                        <span key={d} style={{ fontSize: '0.75rem', fontWeight: '600', color: course.color, background: `${course.color}14`, padding: '4px 10px', borderRadius: '50px' }}>{d}</span>
                      ))}
                    </div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#2b2240', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Modules Covered</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                      {course.modules.map((m) => (
                        <div key={m} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '0.775rem', color: '#4b5563' }}>
                          <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: course.color, flexShrink: 0, marginTop: '6px' }} />{m}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#2b2240', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Placement Roles</div>
                    {course.placementRoles.map((p, i) => (
                      <div key={p} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem', color: '#4b5563', marginBottom: '4px', marginRight: '6px' }}>
                        <Briefcase size={11} style={{ color: course.color }} />{p}{i < course.placementRoles.length - 1 ? ',' : ''}
                      </div>
                    ))}
                  </div>

                  <div style={{ marginBottom: '20px', padding: '12px 14px', background: '#f8f7ff', borderRadius: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px', marginBottom: '10px' }}>
                      <div style={{ fontSize: '0.68rem', color: '#9ca3af' }}>Group Session</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Enrollment fee <strong style={{ color: course.color }}>Rs. 1,000</strong></div>
                    </div>
                    {feeModes.map((f, i) => (
                      <div key={f.mode} style={i ? { marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #ece9f7' } : undefined}>
                        <div style={{ fontSize: '0.72rem', fontWeight: '700', color: '#2b2240', marginBottom: '6px' }}>{f.mode}</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                          {[['Before placement', f.before], ['After placement', f.after], ['Total', f.total]].map(([label, amount]) => (
                            <div key={label} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                              <div style={{ fontSize: '0.68rem', color: '#9ca3af' }}>{label}</div>
                              <div style={{ fontWeight: '800', color: course.color, fontSize: '0.875rem' }}>{amount}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                    <div style={{ fontSize: '0.68rem', color: '#9ca3af', marginTop: '8px' }}>
                      The Rs. 1,000 enrollment fee is part of the fee paid before placement.
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => setBrochureCourse({ title: course.title, file: course.file })}
                      style={{
                        flex: 1, padding: '11px', borderRadius: '10px', border: 'none', cursor: 'pointer',
                        background: course.gradient, color: 'white', fontWeight: '700', fontSize: '0.85rem',
                        fontFamily: 'Poppins, sans-serif',
                      }}
                    >
                      <Download size={15} style={{ marginRight: '6px', display: 'inline' }} />Download Brochure
                    </button>
                    <Link to="/enroll"
                      style={{
                        padding: '11px 18px', borderRadius: '10px', border: `1.5px solid ${course.color}40`,
                        color: course.color, fontWeight: '700', fontSize: '0.85rem', textDecoration: 'none',
                        display: 'flex', alignItems: 'center', whiteSpace: 'nowrap',
                      }}
                    >
                      Enroll Now
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Benefits */}
      <section style={{ padding: '80px 1.5rem', background: '#f8f7ff' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginBottom: '48px' }}
          >
            <h2 style={{ fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)', fontWeight: '800', color: '#2b2240', marginBottom: '10px' }}>
              Every Course Includes
            </h2>
            <p style={{ color: '#6b7280', fontSize: '1rem' }}>Powerful features at no extra cost</p>
          </motion.div>
          <div className="grid-4col-benefits" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
            {commonBenefits.map((b, i) => (
              <motion.div
                key={b}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04 }}
                style={{ background: 'white', border: '1px solid rgba(43,34,64,0.08)', borderRadius: '12px', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'linear-gradient(135deg, #F0B429, #FF6535)', flexShrink: 0 }} />
                <span style={{ fontWeight: '600', color: '#2b2240', fontSize: '0.8rem' }}>{b}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '80px 1.5rem', background: 'linear-gradient(135deg, #0d0b14, #2b2240)', textAlign: 'center' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 style={{ fontSize: 'clamp(1.6rem, 4vw, 2.4rem)', fontWeight: '800', color: 'white', marginBottom: '16px' }}>
              Ready to Start Your Journey?
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '1rem', marginBottom: '32px', lineHeight: 1.7 }}>
              Choose your course, download the brochure, and begin your transformation. Pay 50% now — the rest after you get hired.
            </p>
            <Link to="/enroll"
              style={{ display: 'inline-block', padding: '16px 40px', borderRadius: '12px', background: 'linear-gradient(135deg, #F0B429, #FF6535)', color: 'white', fontWeight: '700', fontSize: '1.05rem', textDecoration: 'none', boxShadow: '0 8px 30px rgba(240,180,41,0.4)' }}
            >
              Get Started Now →
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Courses;
